import React from 'react';
import { ChevronDown, ChevronUp, Loader2, AlertCircle, Package } from 'lucide-react';

// Shared table utility functions and components

export interface TableColumn<T> {
  id: string;
  label: string;
  accessor: (item: T) => React.ReactNode;
  sortable?: boolean;
  minWidth?: string;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

// Generic sort function that works with any data type
export function sortData<T>(
  data: T[],
  sortField: string,
  sortDirection: 'asc' | 'desc',
  columns: TableColumn<T>[]
): T[] {
  if (!sortField) return data;

  const column = columns.find(col => col.id === sortField);
  if (!column) return data;

  return [...data].sort((a, b) => {
    const aValue = column.accessor(a);
    const bValue = column.accessor(b);

    // Handle React components by extracting text content
    const aStr = typeof aValue === 'string' ? aValue : String(aValue);
    const bStr = typeof bValue === 'string' ? bValue : String(bValue);

    if (aStr === bStr) return 0;
    if (!aStr) return 1;
    if (!bStr) return -1;

    const comparison = aStr.localeCompare(bStr, undefined, { numeric: true });
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

// Paginate data
export function paginateData<T>(
  data: T[],
  currentPage: number,
  rowsPerPage: number
): T[] {
  const start = (currentPage - 1) * rowsPerPage;
  return data.slice(start, start + rowsPerPage);
}

// Calculate total pages
export function getTotalPages(totalItems: number, rowsPerPage: number): number {
  return Math.max(1, Math.ceil(totalItems / rowsPerPage));
}

// Shared column header component
interface ColumnHeaderProps {
  column: TableColumn<any>;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
}

export const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  column,
  sortField,
  sortDirection,
  onSort
}) => {
  if (!column.sortable) {
    return <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{column.label}</span>;
  }

  return (
    <button
      className="flex items-center gap-1 hover:text-gray-700 focus:outline-none focus:underline text-xs font-medium text-gray-500 uppercase tracking-wider"
      onClick={() => onSort(column.id)}
      aria-label={`Sort by ${column.label}`}
    >
      {column.label}
      {sortField === column.id && (
        <span className="ml-1">
          {sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      )}
    </button>
  );
};

// Shared column settings dropdown
interface ColumnSettingsDropdownProps {
  columns: TableColumn<any>[];
  visibleColumns: { [key: string]: boolean };
  onToggleColumn: (columnId: string, visible: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const ColumnSettingsDropdown: React.FC<ColumnSettingsDropdownProps> = ({
  columns,
  visibleColumns,
  onToggleColumn,
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-56 p-3 bg-white border rounded shadow-lg z-10">
      <h4 className="text-sm font-semibold mb-2">Toggle columns</h4>
      <div className="space-y-1">
        {columns.map((column) => (
          <div key={column.id} className="flex items-center">
            <input
              type="checkbox"
              id={`col-${column.id}`}
              checked={visibleColumns[column.id]}
              onChange={() => onToggleColumn(column.id, !visibleColumns[column.id])}
              className="mr-2"
            />
            <label htmlFor={`col-${column.id}`} className="text-sm">
              {column.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

// Loading state component
interface TableLoadingStateProps {
  entityName?: string;
}

export const TableLoadingState: React.FC<TableLoadingStateProps> = ({ entityName = "data" }) => (
  <div className="flex items-center justify-center p-8">
    <div className="flex items-center space-x-2">
      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      <span className="text-gray-600">Loading {entityName}...</span>
    </div>
  </div>
);

// Error state component
interface TableErrorStateProps {
  error: string;
  onRetry?: () => void;
}

export const TableErrorState: React.FC<TableErrorStateProps> = ({ error, onRetry }) => (
  <div className="flex flex-col items-center justify-center p-8">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
    <p className="text-gray-600 mb-4 text-center">{error}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
      >
        Try Again
      </button>
    )}
  </div>
);

// Empty state component
interface TableEmptyStateProps {
  entityName: string;
  onClearFilters?: () => void;
}

export const TableEmptyState: React.FC<TableEmptyStateProps> = ({ entityName, onClearFilters }) => (
  <div className="flex flex-col items-center justify-center p-8">
    <Package className="h-12 w-12 text-gray-400 mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">No {entityName} Found</h3>
    <p className="text-gray-600 mb-4 text-center">
      {onClearFilters ? `No ${entityName.toLowerCase()} match your current filters.` : `No ${entityName.toLowerCase()} available.`}
    </p>
    {onClearFilters && (
      <button
        onClick={onClearFilters}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
      >
        Clear Filters
      </button>
    )}
  </div>
);
